<script setup lang="ts">
/**
 * Live clock of a timer: „H:MM:SS“, hours open-ended (`123:04:05`).
 *
 * Shows `accumulatedSeconds` plus, while `running`, the time since
 * `startedAt` (epoch ms, ISO string or `Date`), ticking once a second. The
 * values come from the caller's timer model; the kit keeps no timer state.
 * A paused timer is `running=false`: the clock stands at `accumulatedSeconds`.
 *
 * It is a `role="timer"` element, which screen readers do not read out on
 * every change — a ticking clock would never stop talking. `announceEvery`
 * opts into a polite announcement every N seconds (60 = once a minute).
 */
import { computed, ref, watch } from 'vue';
import NxTabularNumber from '../TabularNumber/NxTabularNumber.vue';
import {
  elapsedSeconds,
  formatElapsed,
  isoDuration,
  toEpochMs,
  useSecondTicker,
  type NxTimestamp,
} from './elapsed';

const props = withDefaults(
  defineProps<{
    /** Start of the current running segment. */
    startedAt?: NxTimestamp | null;
    /** Time banked from earlier segments (pauses), in seconds. */
    accumulatedSeconds?: number;
    running?: boolean;
    /** „01:02:03“ instead of „1:02:03“. */
    padHours?: boolean;
    /** Accessible name of the clock, e.g. „Laufzeit“. */
    ariaLabel?: string;
    /** Announce the time politely every N seconds; `0` (default) never. */
    announceEvery?: number;
  }>(),
  {
    startedAt: null,
    accumulatedSeconds: 0,
    running: true,
    padHours: false,
    ariaLabel: undefined,
    announceEvery: 0,
  }
);

const start = computed(() => toEpochMs(props.startedAt));
const ticking = computed(() => props.running && start.value != null);
const now = useSecondTicker(ticking, () => start.value);

const seconds = computed(() =>
  elapsedSeconds(start.value, props.accumulatedSeconds, now.value, props.running)
);
const text = computed(() => formatElapsed(seconds.value, { padHours: props.padHours }));
const datetime = computed(() => isoDuration(seconds.value));

// Opt-in announcements: only when the clock crosses the next full step.
const announcement = ref('');
watch(
  () => (props.announceEvery > 0 ? Math.floor(seconds.value / props.announceEvery) : -1),
  (step, previous) => {
    if (step > previous && previous >= 0) announcement.value = text.value;
  }
);
</script>

<template>
  <span class="nx-elapsed-time" :class="{ 'is-running': ticking }">
    <time role="timer" class="nx-elapsed-time__clock" :datetime="datetime" :aria-label="ariaLabel">
      <NxTabularNumber :value="text" />
    </time>
    <span
      v-if="announceEvery > 0"
      class="nx-elapsed-time__live"
      aria-live="polite"
      aria-atomic="true"
    >
      {{ announcement }}
    </span>
  </span>
</template>

<style scoped>
.nx-elapsed-time {
  display: inline-flex;
  align-items: baseline;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.nx-elapsed-time__live {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  margin: calc(var(--nxt-hairline) * -1);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
</style>
